import * as i0 from '@angular/core';
import { Injectable, Component, Input, EventEmitter, Output, NgModule } from '@angular/core';
import { formatNumber, CommonModule } from '@angular/common';
import * as i4 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';

class RangeService {
    constructor() {
        this._initControl = new Subject();
    }
    percent(min, max, value) {
        if (min > max) {
            return min;
        }
        const amount = max - min;
        value = this.valueOrDefault(min, max, value) - min;
        return (value * 100) / amount;
    }
    valueOrDefault(min, max, value) {
        if (typeof value === 'number') {
            return value;
        }
        return min > max ? min : min + (max - min) / 2;
    }
    init(min, max, currency, symbol, step) {
        if (currency && symbol === '%') {
            symbol = '$';
        }
        // calculate step if it was not passed
        if (!step) {
            step = (max - min) / 100;
        }
        return { symbol, step };
    }
    initForm(value, onChange) {
        if (!onChange) {
            this._initControl.next();
            return this._initControl.asObservable();
        }
        // need to wait a bit to init FormControls
        setTimeout(() => {
            if (value === undefined) {
                this._initControl.next();
            }
        }, 0);
        return this._initControl.asObservable();
    }
    format(value, currency, symbol) {
        if (currency) {
            return `${symbol}${formatNumber(value, 'en-US', '1.2-2')}`;
        }
        // percent | default formatting
        return `${value}${symbol}`;
    }
    findStepIndex(value, steps) {
        if (!steps.length) {
            return -1;
        }
        let index = steps.indexOf(value);
        if (index < 0) {
            // happens when passing a wrong object reference to component
            // ex.: passing a equal object but recreated, causing not referece problem
            // let's try a deep equality object compare
            index = steps.findIndex((s) => this._isDeepStrictEqual(value, s));
            // not found anyway
            if (index < 0) {
                throw new ReferenceError('Item is not part of steps array!');
            }
        }
        return index;
    }
    _isDeepStrictEqual(obj1, obj2) {
        // try to compare without sorting keys first for better performance
        const equal = JSON.stringify(obj1) === JSON.stringify(obj2);
        if (equal) {
            return true;
        }
        // not equal, try with sorted keys
        return (JSON.stringify(this._sortObjectKeys(obj1)) ===
            JSON.stringify(this._sortObjectKeys(obj2)));
    }
    _sortObjectKeys(obj) {
        return Object.keys(obj)
            .sort()
            .reduce((result, key) => {
            result[key] = obj[key];
            return result;
        }, {});
    }
}
RangeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RangeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class HeaderComponent {
    constructor() {
        this.percent = 50;
        this.disabled = false;
    }
}
HeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
HeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: HeaderComponent, selector: "ps-ui-range-header", inputs: { value: "value", percent: "percent", disabled: "disabled" }, ngImport: i0, template: "<span\n  class=\"value-label text-primary d-inline-block text-nowrap\"\n  [class.text-secondary]=\"disabled\"\n  [style.margin-left.%]=\"percent\"\n  [style.transform]=\"'translate(-' + percent + '%)'\"\n>\n  {{ value }}\n</span>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-range-header', template: "<span\n  class=\"value-label text-primary d-inline-block text-nowrap\"\n  [class.text-secondary]=\"disabled\"\n  [style.margin-left.%]=\"percent\"\n  [style.transform]=\"'translate(-' + percent + '%)'\"\n>\n  {{ value }}\n</span>\n", styles: [""] }]
        }], propDecorators: { value: [{
                type: Input
            }], percent: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

class FooterComponent {
}
FooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: FooterComponent, selector: "ps-ui-range-footer", inputs: { min: "min", max: "max" }, ngImport: i0, template: "<span class=\"min-max float-start text-muted\">{{ min }}</span>\n<span class=\"min-max float-end text-muted\">{{ max }}</span>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-range-footer', template: "<span class=\"min-max float-start text-muted\">{{ min }}</span>\n<span class=\"min-max float-end text-muted\">{{ max }}</span>\n", styles: [""] }]
        }], propDecorators: { min: [{
                type: Input
            }], max: [{
                type: Input
            }] } });

class RangeComponent {
    constructor(_service) {
        this._service = _service;
        this.finalValue = 50;
        this.min = 0;
        this.max = 100;
        this.currency = false;
        this.symbol = '%';
        this.disabled = false;
        this.valueChange = new EventEmitter();
        this.budgetProgressRangeStyle = '--webkitProgressPercent: 50%';
        this.calculateRangeInputProgressPercentage = (currentIndex, min, max) => {
            return Number(((currentIndex - min) * 100) / (max - min));
        };
    }
    get percent() {
        return this._service.percent(this.min, this.max, this.value);
    }
    get valueLabel() {
        return this._format(this._valueOrDefault);
    }
    get minLabel() {
        return this._format(this.min);
    }
    get maxLabel() {
        return this._format(this.max);
    }
    ngOnInit() {
        const { symbol, step } = this._service.init(this.min, this.max, this.currency, this.symbol, this.step);
        this.symbol = symbol;
        this.step = step;
        this._initValue();
    }
    ngOnChanges(changes) {
        var _a;
        const value = (_a = changes.value) === null || _a === void 0 ? void 0 : _a.currentValue;
        if (!value) {
            this._updateRangeUpiutProgressBar(this._valueOrDefault);
        }
        else {
            this._updateRangeUpiutProgressBar(value);
        }
    }
    _updateRangeUpiutProgressBar(value) {
        const leftPercentage = this.calculateRangeInputProgressPercentage(value, this.min, this.max);
        // Workaround for chrome-based browsers
        this.budgetProgressRangeStyle = `--webkitProgressPercent: ${Math.round(leftPercentage)}%`;
    }
    get _valueOrDefault() {
        return this._service.valueOrDefault(this.min, this.max, this.value);
    }
    _initValue() {
        // set default value in the middle if it's not passed to component
        if (!this.value) {
            this.value = this._valueOrDefault;
            this._updateRangeUpiutProgressBar(this.value);
        }
        else {
            this._updateRangeUpiutProgressBar(this.value);
        }
    }
    updateValue(value) {
        this.value = value;
        this._updateRangeUpiutProgressBar(value);
        if (this.onChange) {
            this.onChange(value);
        }
        this.valueChange.emit(value);
    }
    _format(value) {
        if (this.formatter) {
            return this.formatter(value);
        }
        return this._service.format(value, this.currency, this.symbol);
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched() {
        // do nothing. Needed for ControlValueAccessor interface
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
}
RangeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeComponent, deps: [{ token: RangeService }], target: i0.ɵɵFactoryTarget.Component });
RangeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: RangeComponent, selector: "ps-ui-range", inputs: { value: "value", min: "min", max: "max", step: "step", currency: "currency", symbol: "symbol", disabled: "disabled", formatter: "formatter" }, outputs: { valueChange: "valueChange" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: RangeComponent,
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0, template: "<ps-ui-range-header\n  [disabled]=\"disabled\"\n  [percent]=\"percent\"\n  [value]=\"valueLabel\"\n></ps-ui-range-header>\n<input\n  type=\"range\"\n  class=\"form-range range-slider\"\n  [min]=\"min\"\n  [max]=\"max\"\n  [step]=\"step\"\n  [ngModel]=\"value\"\n  [disabled]=\"disabled\"\n  [style]=\"budgetProgressRangeStyle\"\n  (ngModelChange)=\"updateValue($event)\"\n/>\n<ps-ui-range-footer [min]=\"minLabel\" [max]=\"maxLabel\"> </ps-ui-range-footer>\n", styles: [".range-slider{height:5px;--thumbSize: 10px;--trackSize: 5px;--range-thumb-bg: #31b2f1;--range-track-bg: #ebebec;--range-progress-bg: #31b2f1;--webkitProgressPercent: 50%;-webkit-appearance:none}.range-slider:focus{outline:none}.range-slider::-moz-range-thumb{background:var(--range-thumb-bg);height:var(--thumbSize);width:var(--thumbSize);border:none;cursor:pointer}.range-slider::-moz-range-progress{background:var(--range-progress-bg)}.range-slider::-moz-range-track{background:var(--range-track-bg);border-color:#e7e7e7;border:1px}.range-slider::-webkit-slider-runnable-track{height:var(--trackSize);background-image:linear-gradient(90deg,var(--range-progress-bg) var(--webkitProgressPercent),var(--range-track-bg) var(--webkitProgressPercent));border-radius:calc(var(--trackSize) / 2)}.range-slider::-webkit-slider-thumb{appearance:none;width:var(--thumbSize);height:var(--thumbSize);background-color:var(--range-thumb-bg);border-radius:calc(var(--thumbSize) / 2);border:none;margin-top:calc(((var(--thumbSize) - var(--trackSize)) / 2) * -1);cursor:pointer}\n"], components: [{ type: HeaderComponent, selector: "ps-ui-range-header", inputs: ["value", "percent", "disabled"] }, { type: FooterComponent, selector: "ps-ui-range-footer", inputs: ["min", "max"] }], directives: [{ type: i4.RangeValueAccessor, selector: "input[type=range][formControlName],input[type=range][formControl],input[type=range][ngModel]" }, { type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-range', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: RangeComponent,
                            multi: true,
                        },
                    ], template: "<ps-ui-range-header\n  [disabled]=\"disabled\"\n  [percent]=\"percent\"\n  [value]=\"valueLabel\"\n></ps-ui-range-header>\n<input\n  type=\"range\"\n  class=\"form-range range-slider\"\n  [min]=\"min\"\n  [max]=\"max\"\n  [step]=\"step\"\n  [ngModel]=\"value\"\n  [disabled]=\"disabled\"\n  [style]=\"budgetProgressRangeStyle\"\n  (ngModelChange)=\"updateValue($event)\"\n/>\n<ps-ui-range-footer [min]=\"minLabel\" [max]=\"maxLabel\"> </ps-ui-range-footer>\n", styles: [".range-slider{height:5px;--thumbSize: 10px;--trackSize: 5px;--range-thumb-bg: #31b2f1;--range-track-bg: #ebebec;--range-progress-bg: #31b2f1;--webkitProgressPercent: 50%;-webkit-appearance:none}.range-slider:focus{outline:none}.range-slider::-moz-range-thumb{background:var(--range-thumb-bg);height:var(--thumbSize);width:var(--thumbSize);border:none;cursor:pointer}.range-slider::-moz-range-progress{background:var(--range-progress-bg)}.range-slider::-moz-range-track{background:var(--range-track-bg);border-color:#e7e7e7;border:1px}.range-slider::-webkit-slider-runnable-track{height:var(--trackSize);background-image:linear-gradient(90deg,var(--range-progress-bg) var(--webkitProgressPercent),var(--range-track-bg) var(--webkitProgressPercent));border-radius:calc(var(--trackSize) / 2)}.range-slider::-webkit-slider-thumb{appearance:none;width:var(--thumbSize);height:var(--thumbSize);background-color:var(--range-thumb-bg);border-radius:calc(var(--thumbSize) / 2);border:none;margin-top:calc(((var(--thumbSize) - var(--trackSize)) / 2) * -1);cursor:pointer}\n"] }]
        }], ctorParameters: function () { return [{ type: RangeService }]; }, propDecorators: { value: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], currency: [{
                type: Input
            }], symbol: [{
                type: Input
            }], disabled: [{
                type: Input
            }], formatter: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class RangeListComponent {
    constructor(_service) {
        this._service = _service;
        this.min = 0;
        this.max = 100;
        this.step = 1;
        this.currency = false;
        this.symbol = '';
        this.disabled = false;
        this.valueChange = new EventEmitter();
        this.budgetProgressRangeStyle = '--webkitProgressPercent: 50%';
        this.calculateRangeInputProgressPercentage = (currentIndex, min, max) => {
            return Number(((currentIndex - min) * 100) / (max - min));
        };
    }
    get percent() {
        return this._service.percent(this.min, this.max, this.value);
    }
    get valueLabel() {
        return this._format(this._indexOrDefault);
    }
    get minLabel() {
        return this._format(this.min);
    }
    get maxLabel() {
        return this._format(this.max);
    }
    ngOnInit() {
        // made step numbers unique
        this.max = this.steps.length - 1;
        this._service.init(this.min, this.max, this.currency, this.symbol, this.step);
        this._initValue();
    }
    ngOnChanges(changes) {
        var _a;
        const value = (_a = changes.value) === null || _a === void 0 ? void 0 : _a.currentValue;
        if (!value) {
            this._updateRangeUpiutProgressBar(this._indexOrDefault);
        }
        else {
            this._updateRangeUpiutProgressBar(value);
        }
    }
    _updateRangeUpiutProgressBar(value) {
        const leftPercentage = this.calculateRangeInputProgressPercentage(value, this.min, this.max);
        // Workaround for chrome-based browsers
        this.budgetProgressRangeStyle = `--webkitProgressPercent: ${Math.round(leftPercentage)}%`;
    }
    get _indexOrDefault() {
        let index = this.value && this.value < 0 ? undefined : this.value;
        index = this._service.valueOrDefault(this.min, this.max, index);
        return this._fixIndex(index);
    }
    _fixIndex(value) {
        // let's cast into a integer if the value is a float
        if (!Number.isInteger(value)) {
            return Math.floor(value);
        }
        return value;
    }
    _initValue() {
        // set default value in the middle if it's not passed to component
        if (!this.value && this.value != 0) {
            this.updateValue(this._indexOrDefault);
        }
        else {
            this._updateRangeUpiutProgressBar(this.value);
        }
    }
    updateValue(index) {
        this.value = index;
        this._updateRangeUpiutProgressBar(index);
        if (this.onChange) {
            this.onChange(this.value);
        }
        this.valueChange.emit(this._fixIndex(index));
    }
    _format(index) {
        const value = this.steps[index];
        if (this.formatter) {
            return this.formatter(value);
        }
        return this._service.format(value, this.currency, this.symbol);
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched() {
        // do nothing. Needed for ControlValueAccessor interface
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
}
RangeListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeListComponent, deps: [{ token: RangeService }], target: i0.ɵɵFactoryTarget.Component });
RangeListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: RangeListComponent, selector: "ps-ui-range-list [steps]", inputs: { value: "value", steps: "steps", currency: "currency", symbol: "symbol", disabled: "disabled", formatter: "formatter" }, outputs: { valueChange: "valueChange" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: RangeListComponent,
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0, template: "<ps-ui-range-header\n  [disabled]=\"disabled\"\n  [percent]=\"percent\"\n  [value]=\"valueLabel\"\n></ps-ui-range-header>\n<input\n  type=\"range\"\n  class=\"form-range range-slider\"\n  [min]=\"min\"\n  [max]=\"max\"\n  [step]=\"step\"\n  [ngModel]=\"value\"\n  [disabled]=\"disabled\"\n  [style]=\"budgetProgressRangeStyle\"\n  (ngModelChange)=\"updateValue($event)\"\n/>\n<ps-ui-range-footer [min]=\"minLabel\" [max]=\"maxLabel\"> </ps-ui-range-footer>\n", styles: [".range-slider{height:5px;--thumbSize: 10px;--trackSize: 5px;--range-thumb-bg: #31b2f1;--range-track-bg: #ebebec;--range-progress-bg: #31b2f1;--webkitProgressPercent: 50%;-webkit-appearance:none}.range-slider:focus{outline:none}.range-slider::-moz-range-thumb{background:var(--range-thumb-bg);height:var(--thumbSize);width:var(--thumbSize);border:none;cursor:pointer}.range-slider::-moz-range-progress{background:var(--range-progress-bg)}.range-slider::-moz-range-track{background:var(--range-track-bg);border-color:#e7e7e7;border:1px}.range-slider::-webkit-slider-runnable-track{height:var(--trackSize);background-image:linear-gradient(90deg,var(--range-progress-bg) var(--webkitProgressPercent),var(--range-track-bg) var(--webkitProgressPercent));border-radius:calc(var(--trackSize) / 2)}.range-slider::-webkit-slider-thumb{appearance:none;width:var(--thumbSize);height:var(--thumbSize);background-color:var(--range-thumb-bg);border-radius:calc(var(--thumbSize) / 2);border:none;margin-top:calc(((var(--thumbSize) - var(--trackSize)) / 2) * -1);cursor:pointer}\n"], components: [{ type: HeaderComponent, selector: "ps-ui-range-header", inputs: ["value", "percent", "disabled"] }, { type: FooterComponent, selector: "ps-ui-range-footer", inputs: ["min", "max"] }], directives: [{ type: i4.RangeValueAccessor, selector: "input[type=range][formControlName],input[type=range][formControl],input[type=range][ngModel]" }, { type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i4.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-range-list [steps]', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: RangeListComponent,
                            multi: true,
                        },
                    ], template: "<ps-ui-range-header\n  [disabled]=\"disabled\"\n  [percent]=\"percent\"\n  [value]=\"valueLabel\"\n></ps-ui-range-header>\n<input\n  type=\"range\"\n  class=\"form-range range-slider\"\n  [min]=\"min\"\n  [max]=\"max\"\n  [step]=\"step\"\n  [ngModel]=\"value\"\n  [disabled]=\"disabled\"\n  [style]=\"budgetProgressRangeStyle\"\n  (ngModelChange)=\"updateValue($event)\"\n/>\n<ps-ui-range-footer [min]=\"minLabel\" [max]=\"maxLabel\"> </ps-ui-range-footer>\n", styles: [".range-slider{height:5px;--thumbSize: 10px;--trackSize: 5px;--range-thumb-bg: #31b2f1;--range-track-bg: #ebebec;--range-progress-bg: #31b2f1;--webkitProgressPercent: 50%;-webkit-appearance:none}.range-slider:focus{outline:none}.range-slider::-moz-range-thumb{background:var(--range-thumb-bg);height:var(--thumbSize);width:var(--thumbSize);border:none;cursor:pointer}.range-slider::-moz-range-progress{background:var(--range-progress-bg)}.range-slider::-moz-range-track{background:var(--range-track-bg);border-color:#e7e7e7;border:1px}.range-slider::-webkit-slider-runnable-track{height:var(--trackSize);background-image:linear-gradient(90deg,var(--range-progress-bg) var(--webkitProgressPercent),var(--range-track-bg) var(--webkitProgressPercent));border-radius:calc(var(--trackSize) / 2)}.range-slider::-webkit-slider-thumb{appearance:none;width:var(--thumbSize);height:var(--thumbSize);background-color:var(--range-thumb-bg);border-radius:calc(var(--thumbSize) / 2);border:none;margin-top:calc(((var(--thumbSize) - var(--trackSize)) / 2) * -1);cursor:pointer}\n"] }]
        }], ctorParameters: function () { return [{ type: RangeService }]; }, propDecorators: { value: [{
                type: Input
            }], steps: [{
                type: Input
            }], currency: [{
                type: Input
            }], symbol: [{
                type: Input
            }], disabled: [{
                type: Input
            }], formatter: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class RangeModule {
}
RangeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RangeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeModule, declarations: [RangeComponent,
        RangeListComponent,
        FooterComponent,
        HeaderComponent], imports: [CommonModule, FormsModule], exports: [RangeComponent, RangeListComponent] });
RangeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeModule, imports: [[CommonModule, FormsModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RangeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        RangeComponent,
                        RangeListComponent,
                        FooterComponent,
                        HeaderComponent,
                    ],
                    exports: [RangeComponent, RangeListComponent],
                    imports: [CommonModule, FormsModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RangeComponent, RangeListComponent, RangeModule };
